import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Shield,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  type CreditDecisionOutcome,
  useCreditDecisions,
  useLoanSummary,
  useRecordCreditDecision,
} from '@/hooks/use-credit-decisions';
import { handleMutationError } from '@/lib/error-handling';
import { toast } from '@/stores/toast-store';
import type { LoanStatus } from '@/types';

function fmtCurrency(v: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  }).format(v);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DECISION_CONFIG: Record<
  CreditDecisionOutcome,
  { icon: typeof CheckCircle2; color: string; bg: string; label: string }
> = {
  APPROVED: {
    icon: CheckCircle2,
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    label: 'Approved',
  },
  DECLINED: {
    icon: XCircle,
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    label: 'Declined',
  },
  INFO_REQUIRED: {
    icon: AlertCircle,
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    label: 'Info Required',
  },
};

interface Props {
  loanId: string;
  loanStatus: LoanStatus;
}

export function CreditDecisionPanel({ loanId, loanStatus }: Props) {
  const { data: decisions, isLoading: decisionsLoading } = useCreditDecisions(loanId);
  const { data: summary, isLoading: summaryLoading } = useLoanSummary(loanId);
  const recordDecision = useRecordCreditDecision();

  const [showForm, setShowForm] = useState(false);
  const [decision, setDecision] = useState<CreditDecisionOutcome>('APPROVED');
  const [reason, setReason] = useState('');
  const [conditions, setConditions] = useState('');
  const [infoRequired, setInfoRequired] = useState('');
  const [showSummary, setShowSummary] = useState(true);

  const canDecide = loanStatus === 'UNDER_REVIEW';

  function handleSubmit() {
    if (!reason.trim()) {
      toast.error('Please provide a reason for the decision');
      return;
    }
    if (decision === 'INFO_REQUIRED' && !infoRequired.trim()) {
      toast.error('Please specify what information is required');
      return;
    }

    recordDecision.mutate(
      {
        loanId,
        decision,
        reason: reason.trim(),
        conditions: decision === 'APPROVED' ? conditions.trim() || undefined : undefined,
        infoRequired: decision === 'INFO_REQUIRED' ? infoRequired.trim() : undefined,
      },
      {
        onSuccess: () => {
          toast.success(`Credit decision recorded: ${DECISION_CONFIG[decision].label}`);
          setShowForm(false);
          setReason('');
          setConditions('');
          setInfoRequired('');
        },
        onError: (err: any) => {
          handleMutationError(err, 'Failed to record decision');
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary toggle */}
      {summary && !summaryLoading && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setShowSummary(!showSummary)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-500" />
              Loan Application Summary
            </span>
            {showSummary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showSummary && (
            <div className="px-4 pb-4 space-y-4 border-t border-gray-100">
              {/* Key financials */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Principal</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {fmtCurrency(summary.loan.principal)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Interest Rate</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {summary.loan.interestRate.toFixed(2)}%
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Term</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {summary.loan.termMonths} months
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Frequency</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {summary.loan.paymentFrequency.toLowerCase()}
                  </p>
                </div>
              </div>

              {/* Borrower */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Borrower</h4>
                <div className="text-sm text-gray-900">{summary.borrower.name}</div>
                {summary.borrower.email && (
                  <div className="text-xs text-gray-500">{summary.borrower.email}</div>
                )}
                {summary.borrower.phone && (
                  <div className="text-xs text-gray-500">{summary.borrower.phone}</div>
                )}
              </div>

              {/* Security summary */}
              {summary.securities.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Security</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600">Total Value</p>
                      <p className="text-sm font-semibold text-blue-900">
                        {fmtCurrency(summary.securitySummary.totalSecurityValue)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600">Existing Debt</p>
                      <p className="text-sm font-semibold text-blue-900">
                        {fmtCurrency(summary.securitySummary.totalExistingDebt)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs text-blue-600">Net Equity</p>
                      <p className="text-sm font-semibold text-blue-900">
                        {fmtCurrency(summary.securitySummary.netEquity)}
                      </p>
                    </div>
                    {summary.securitySummary.ltv !== null && (
                      <div
                        className={`rounded-lg p-3 ${summary.securitySummary.ltv > 80 ? 'bg-red-50' : 'bg-green-50'}`}
                      >
                        <p
                          className={`text-xs ${summary.securitySummary.ltv > 80 ? 'text-red-600' : 'text-green-600'}`}
                        >
                          LTV Ratio
                        </p>
                        <p
                          className={`text-sm font-semibold ${summary.securitySummary.ltv > 80 ? 'text-red-900' : 'text-green-900'}`}
                        >
                          {summary.securitySummary.ltv.toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 space-y-1">
                    {summary.securities.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-xs text-gray-600 py-1"
                      >
                        <span>
                          {s.description} ({s.type.replace(/_/g, ' ').toLowerCase()})
                        </span>
                        <span className="font-medium">
                          {fmtCurrency(
                            s.valuerEstimate ?? s.internalEstimate ?? s.borrowerValuation ?? 0,
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fees & costs */}
              {(summary.loan.establishmentFee > 0 ||
                summary.loan.brokerageFee > 0 ||
                summary.loan.legalCosts > 0) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Fees & Costs
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {summary.loan.establishmentFee > 0 && (
                      <div className="text-xs">
                        <span className="text-gray-500">Establishment:</span>{' '}
                        <span className="font-medium">
                          {fmtCurrency(summary.loan.establishmentFee)}
                        </span>
                      </div>
                    )}
                    {summary.loan.brokerageFee > 0 && (
                      <div className="text-xs">
                        <span className="text-gray-500">Brokerage:</span>{' '}
                        <span className="font-medium">
                          {fmtCurrency(summary.loan.brokerageFee)}
                        </span>
                      </div>
                    )}
                    {summary.loan.legalCosts > 0 && (
                      <div className="text-xs">
                        <span className="text-gray-500">Legal:</span>{' '}
                        <span className="font-medium">{fmtCurrency(summary.loan.legalCosts)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Next 6 repayments */}
              {summary.schedule.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    Repayment Schedule (First {summary.schedule.length})
                  </h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b">
                        <th className="text-left py-1">#</th>
                        <th className="text-left py-1">Due Date</th>
                        <th className="text-right py-1">Principal</th>
                        <th className="text-right py-1">Interest</th>
                        <th className="text-right py-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.schedule.map(s => (
                        <tr key={s.periodNumber} className="border-b border-gray-50">
                          <td className="py-1 text-gray-500">{s.periodNumber}</td>
                          <td className="py-1">{fmtDate(s.dueDate)}</td>
                          <td className="py-1 text-right">{fmtCurrency(s.principalDue)}</td>
                          <td className="py-1 text-right">{fmtCurrency(s.interestDue)}</td>
                          <td className="py-1 text-right font-medium">{fmtCurrency(s.totalDue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Decision form for UNDER_REVIEW loans */}
      {canDecide && (
        <div className="bg-white rounded-lg border border-indigo-200 p-4">
          {!showForm ? (
            <Button
              variant="primary"
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium hover: transition-colors w-full justify-center"
            >
              <Shield className="w-4 h-4" />
              Record Credit Decision
            </Button>
          ) : (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Credit Committee Decision</h3>

              {/* Decision buttons */}
              <div className="flex gap-2">
                {(['APPROVED', 'DECLINED', 'INFO_REQUIRED'] as CreditDecisionOutcome[]).map(d => {
                  const cfg = DECISION_CONFIG[d];
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={d}
                      onClick={() => setDecision(d)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        decision === d
                          ? cfg.bg + ' ' + cfg.color + ' border-current'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason / Committee Notes *
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Summarise the committee's rationale..."
                />
              </div>

              {/* Conditions (if approved) */}
              {decision === 'APPROVED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conditions of Approval
                  </label>
                  <textarea
                    value={conditions}
                    onChange={e => setConditions(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any conditions the borrower must meet..."
                  />
                </div>
              )}

              {/* Info required */}
              {decision === 'INFO_REQUIRED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Information Required *
                  </label>
                  <textarea
                    value={infoRequired}
                    onChange={e => setInfoRequired(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe what additional information is needed..."
                  />
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={recordDecision.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium hover: disabled:opacity-50 transition-colors"
                >
                  {recordDecision.isPending && (
                    <Spinner size="lg" className="w-4 h-4 animate-spin" />
                  )}
                  Submit Decision
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decision history */}
      {decisionsLoading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
          <Spinner size="lg" className="w-4 h-4 animate-spin" /> Loading decisions...
        </div>
      ) : decisions && decisions.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">Decision History</h3>
          {decisions.map(d => {
            const cfg = DECISION_CONFIG[d.decision];
            const Icon = cfg.icon;
            return (
              <div key={d.id} className={`rounded-lg border p-3 ${cfg.bg}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`flex items-center gap-1.5 text-sm font-semibold ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                    {cfg.label}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {fmtDateTime(d.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  By {d.decidedBy.firstName} {d.decidedBy.lastName}
                </p>
                {d.reason && <p className="text-sm text-gray-800 mt-1">{d.reason}</p>}
                {d.conditions && (
                  <div className="mt-2 text-xs bg-white/60 rounded p-2">
                    <span className="font-medium">Conditions:</span> {d.conditions}
                  </div>
                )}
                {d.infoRequired && (
                  <div className="mt-2 text-xs bg-white/60 rounded p-2">
                    <span className="font-medium">Info Required:</span> {d.infoRequired}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
